import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from "axios";
// import GroupedBarChart from "./GroupedCreditCardBarChart";
import GroupedBarChart from "./GroupedRevolverBarChart2";
import {formatCurrency, formatRate, calculateWeightedAvgInterestRate} from "./CoreFunctions";

const RevolversPage = () => {
    const [loading, setLoading] = useState(true);
    const [revolvers, setLoans] = useState([]); // Correctly destructured state
    const [chartData, setChartData] = useState(null); // will hold the array of objects
    const [topChartData, setTopChartData] = useState(null);

    useEffect(() => {
        // Fetch the revolvers from the backend
        const fetchRevolvers = async () => {
            try {
                const response = await axios.get('/api/balance_sheet/financing/revolvers/');
                setLoans(response.data);
            } catch (error) {
                console.error("Error fetching revolvers:", error);
            }
        };

        fetchRevolvers();
    }, []); // Run on component mount

    useEffect(() => {
      console.log('RevolversPage: Fetching statement summary data');
      axios.get(`/api/balance_sheet/financing/revolvers/statement-summary-data/`)
        .then(response => {
          const data = response.data;
          console.log('RevolversPage: Received data, length=', data?.length);
          if (!Array.isArray(data)) {
            console.error('RevolversPage: Data is not an array', data);
            return;
          }
          const grouped = {};

          // Step 1: Group by Year-Month
          data.forEach(item => {
            const year = item.statement_year_int;
            const month = item.statement_month_int;
            const key = `${year}-${month}`;

            if (!grouped[key]) {
              // generate label like "Apr 2025"
              const monthNames = [
                'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
              ];
              const label = `${monthNames[month - 1]} ${year}`;

              grouped[key] = {
                label,
                year,
                month,
                // For the first pass, store raw arrays to compute later
                balanceSubjectToIr: [],
                interestRates: [],
                interestCharged: [],
                balances: [],
                // Also store other needed accumulate fields if necessary
                previous_balance: 0,
                transactions: 0,
                payments: 0,
                balance_subject_to_interest_rate: 0,
                new_balance: 0,
                breakdown: {}, // { type: { name: amount } }
              };
            }

            // Populate fields
            grouped[key].balanceSubjectToIr.push(Number(item.balance_subject_to_interest_rate));
            grouped[key].interestRates.push(Number(item.purchases_apr));
            grouped[key].interestCharged.push(Number(item.interest_charged));
            // Add other sums if needed for main chart
            grouped[key].previous_balance += Number(item.previous_balance) || 0;
            grouped[key].transactions += Number(item.transactions) || 0;
            const payPlusCred = (Number(item.payments) || 0) + (Number(item.other_credits) || 0);
            grouped[key].payments += payPlusCred;
            grouped[key].balance_subject_to_interest_rate += Number(item.balance_subject_to_interest_rate) || 0;
            grouped[key].new_balance += Number(item.new_balance) || 0;

            // Populate breakdown for tooltips
            const type = (item.revolver_type || 'Unknown').replace(/_/g, ' ');
            const name = item.revolver_name || 'Unnamed';
            if (!grouped[key].breakdown[type]) {
              grouped[key].breakdown[type] = {};
            }
            grouped[key].breakdown[type][name] = (grouped[key].breakdown[type][name] || 0) + (Number(item.new_balance) || 0);
          });

          // Sort keys chronologically
          const sortedKeys = Object.keys(grouped).sort((a, b) => {
            const gA = grouped[a];
            const gB = grouped[b];
            if (gA.year !== gB.year) return gA.year - gB.year;
            return gA.month - gB.month;
          });

          // Step 2: Compute interest arrays and interest rate (weighted) per period
          const finalData = sortedKeys.map(key => {
            const g = grouped[key];

            // Calculate weighted interest rate for this group
            const weightedInterestRate = calculateWeightedAvgInterestRate(
              g.balanceSubjectToIr,
              g.interestRates
            );

            // Prepare object with interest charged and weighted interest rate
            return {
              period: g.label,
              interest_charged: g.interestCharged.reduce((a,b) => a + b, 0), // sum if needed
              weighted_interest_rate: weightedInterestRate,
              // Also, include other params used for main chart if required
              previous_balance: g.previous_balance,
              transactions: g.transactions,
              payments: g.payments,
              balance_subject_to_interest_rate: g.balance_subject_to_interest_rate,
              new_balance: g.new_balance,
              breakdown: g.breakdown,
            };
          });

          setChartData(finalData);
          console.log('RevolversPage: finalData for chart=', finalData);
        })
        .catch(error => {
          console.error('Error fetching data:', error);
        });
    }, []);

    // const handleRemove = async (id, loanType) => {
    //     let deleteUrl = '';
    //
    //     if (loanType === 'credit_card') {
    //         deleteUrl = `/api/balance_sheet/financing/revolvers/credit_cards/${id}/`;
    //     } else if (loanType === 'auto_loan') {
    //         deleteUrl = `/api/balance_sheet/financing/revolvers/helocs/${id}/`;
    //     }
    //
    //     try {
    //         await axios.delete(deleteUrl); // Call the appropriate DELETE API
    //         setLoans((prevLoans) => prevLoans.filter(loan => loan.id !== id)); // Update the state
    //         alert("Loan removed successfully");
    //     } catch (error) {
    //         console.error("Error removing loan:", error);
    //         alert("There was an error removing the loan.");
    //     }
    // };

    // Collect data needed to calculate weighted average current interest rate on debt portfolio
    const allOutstandingAmountsArray = [];
    const allRatesArray = [];

    // Collect data needed to calculate weighted average current interest rate on debt portfolio
    const currentOutstandingAmountsArray = [];
    const currentRatesArray = [];

    revolvers.forEach(revolver => {
      currentOutstandingAmountsArray.push(revolver.current_outstanding);
      currentRatesArray.push(revolver.current_rate);
    });

    const sumProduct = currentOutstandingAmountsArray.reduce((acc, amt, index) => {
      return acc + amt * currentRatesArray[index];
    }, 0);

    const totalOutstanding = currentOutstandingAmountsArray.reduce((acc, amt) => acc + amt, 0);

    const weightedAvgRate = totalOutstanding ? (sumProduct / totalOutstanding) : 0;

    // Calculate total loan amount, converting values to numbers
    const totalRevolversAmount = revolvers.reduce((total, revolver) => {
        return total + (parseFloat(revolver.current_credit_limit) || 0); // Convert to a float, default to 0 if NaN
    }, 0);

    const totalOutstandingAmount = revolvers.reduce((total, revolver) => {
        // Add a check for recent_payment to avoid accessing properties of null
        return total + (parseFloat(revolver.current_outstanding) || 0);
    }, 0);

    const totalBalanceSubjectoIr = revolvers.reduce((total, revolver) => {
        // Add a check for recent_payment to avoid accessing properties of null
        return total + (parseFloat(revolver.current_balance_subject_to_ir) || 0);
    }, 0);
    //
    // const totalPrincipalPaid = revolvers.reduce((total, loan) => {
    //     return total + (loan.recent_payment ? parseFloat(loan.recent_payment.cumulative_principal_payment_amt) || 0 : 0);
    // }, 0);
    //
    // const totalInterestPaid = revolvers.reduce((total, loan) => {
    //     return total + (loan.recent_payment ? parseFloat(loan.recent_payment.cumulative_interest_payment_amt) || 0 : 0);
    // }, 0);
    //
    // const totalClosingCosts = revolvers.reduce((total, loan) => {
    //     return total + (parseFloat(loan.total_closing_costs) || 0); // Convert to a float, default to 0 if NaN
    // }, 0);
    //
    // const totalCostOfLoans = (totalInterestPaid != null && totalClosingCosts != null)
    // ? (totalInterestPaid + totalClosingCosts).toFixed(2)
    // : '0.00';
    //
    // const percentageOutstanding = totalRevolversAmount > 0
    //     ? ((totalOutstandingAmount / totalRevolversAmount) * 100).toFixed(2) // Calculate as percentage and format to 2 decimal places
    //     : '0.00';
    //
    // const totalInterestPaidPercentage = totalRevolversAmount > 0
    //     ? ((totalInterestPaid / totalRevolversAmount) * 100).toFixed(2) // Calculate as percentage and format to 2 decimal places
    //     : '0.00';
    //
    // const totalCostOfLoansPercentage = totalRevolversAmount > 0
    //     ? ((totalCostOfLoans / totalRevolversAmount) * 100).toFixed(2) // Calculate as percentage and format to 2 decimal places
    //     : '0.00';

    return (
        <div className="page-container" style={{ marginLeft: '40px' }}>
            <div style={{ marginBottom: '25px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <h1 style={{ marginRight: '20px' }}> Total Revolving Debt </h1>
                <div>
                    <Link to="/balance_sheet/financing/"> All Debt </Link>
                </div>
                <Link to="/balance_sheet/financing/create-loan/">
                    <button
                        type="button"
                        className="btn btn-primary"
                        style={{marginLeft: '20px'}}
                    >Add New Revolving Debt</button>
                </Link>
            </div>
            </div>

            <table className="revolver-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Loan ID</th>
                        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Revolving Debt Type</th>
                        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Lender</th>
                        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Credit Limit</th>
                        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Outstanding Amt.</th>
                        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Balance Subject to IR</th>
                        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Current Rate</th>
                        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Latest Statement</th>
                    </tr>
                </thead>
                <tbody>
                    {revolvers.length > 0 ? (
                        revolvers.map(revolver => (
                            <tr key={revolver.id}>
                                <td style={{ border: '1px solid #ccc', padding: '10px' }}>
                                    <Link to={`${revolver.revolver_type.replace(/_/g, '-')}s/${revolver.id}/`}>{revolver.id}</Link>
                                </td>
                                <td style={{ border: '1px solid #ccc', padding: '10px' }}>
                                    <Link to={`${revolver.revolver_type.replace(/_/g, '-')}s`}>{revolver.revolver_type.replace(/_/g, '-')}</Link>
                                </td>
                                <td style={{ border: '1px solid #ccc', padding: '10px' }}>{revolver.lender_name}</td>
                                <td style={{ border: '1px solid #ccc', padding: '10px' }}>{formatCurrency(revolver.current_credit_limit, 0)}</td>
                                <td style={{ border: '1px solid #ccc', padding: '10px' }}>{formatCurrency(revolver.current_outstanding, 0)}</td>
                                <td style={{ border: '1px solid #ccc', padding: '10px' }}>{formatCurrency(revolver.current_balance_subject_to_ir, 0)}</td>
                                <td style={{ border: '1px solid #ccc', padding: '10px' }}>{formatRate(revolver.current_rate, 2)}</td>
                                <td style={{ border: '1px solid #ccc', padding: '10px' }}>{revolver.latest_statement_date || 'N/A'}</td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="8" style={{ textAlign: 'center', border: '1px solid #ccc', padding: '10px' }}>No revolvers found.</td>
                        </tr>
                    )}
                    {/* Total row */}
                    <tr className="total-row">
                        <td style={{ padding: '10px' }}><strong>Total</strong></td>
                        <td></td>
                        <td></td>
                        <td style={{ padding: '10px' }}><strong>{formatCurrency(totalRevolversAmount, 0)}</strong></td>
                        <td style={{ padding: '10px' }}><strong>{formatCurrency(totalOutstandingAmount, 0)}</strong></td>
                        <td style={{ padding: '10px' }}><strong>{formatCurrency(totalBalanceSubjectoIr, 0)}</strong></td>
                        <td style={{ padding: '10px' }}><strong>{formatRate(weightedAvgRate, 2)}</strong></td>
                        <td></td>
                    </tr>
                </tbody>
            </table>
            <br/>
            <br/>
            <div style={{ width: '100%', minHeight: '700px' }}>
              {chartData ? <GroupedBarChart data={chartData} /> : <p>Loading chart data...</p>}
            </div>
            <style jsx>{`
                .page-container {
                    margin: 20px;
                    width: 90%;
                    table-layout: auto;
                    border-collapse: collapse;
                }
                .debt-table {
                    width: 100%;
                    border-collapse: collapse;
                }
                .debt-table th, .debt-table td {
                    border: 1px solid #ccc;
                    padding: 10px;
                    text-align: left;
                }
                .debt-table th {
                    background-color: #f0f0f0;
                }
                .total-row td {
                    border: none; /* Remove borders for the total row */
                }
                button {
                    background-color: #ff4d4d;
                    color: white;
                    border: none;
                    padding: 5px 10px;
                    cursor: pointer;
                }
                button:hover {
                    background-color: #ff0000;
                }
            `}</style>
        </div>
    );
};

export default RevolversPage;